import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, RowNode } from 'ag-grid-community';
import Swal from 'sweetalert2';

import { NSCommons } from '@ruby/configs/common.constants';
import { NSEBillConstants } from '@ruby/configs/ebill.constants';
import { NSMessages } from '@ruby/configs/messages.constants';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { CommentTableEntry, DisputeTableEntry, IEBillCarDetails, IEBillCarDetailsDispute } from '@ruby/shared/models/ebill/ebil.interface';

@Component({
  selector: 'ng-ebill-car-dispute',
  templateUrl: './ebill-car-dispute.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EBillCarDisputeComponent {
  @Input() invoiceNumber?: string;
  @Input() carDetails!: Array<IEBillCarDetails>;
  @Input() billSequence?: string;
  @Input() loading!: boolean;
  @Input() isDisputeDisabled!: boolean;
  @Output() dispute = new EventEmitter<Partial<IEBillCarDetailsDispute>>();
  @Output() toggleDisputeButton = new EventEmitter();
  gridConfig = NSEBillConstants.gridConfig;
  gridOptions: GridOptions = NSEBillConstants.carDisputeGridOptions;
  selectedRows: Array<RowNode> = [];
  grid!: AgGridAngular;

  constructor(private location: Location) {}

  /**
   * Set Up Grid
   *
   * @summary Sets the Grid reference and filter
   * @param $grid: AgGridAngular
   * @return void
   */
  setUpGrid($grid: AgGridAngular): void {
    this.grid = $grid;
  }

  /**
   * Selection Change
   *
   * @summary Enable/Disable the dispute button when at least one row is selected
   * @param $rows: Array<RowNode>
   * @returns void
   */
  selectionChange($rows: Array<RowNode>): void {
    this.selectedRows = $rows;
    if (this.isDisputeDisabled !== !($rows.length > 0 && $rows.every(
      (node: RowNode) => node.data.newOrderedDate || node.data.newConstraDate || node.data.newPlacedData || node.data.newReleasedDate || node.data.comment
    ))) {
      this.toggleDisputeButton.emit();
    }
  }

  /**
   * Submit Dispute
   *
   * @summary submit disputed bills
   * @return void
   */
  submitDispute(): void {
    const commentEntry: Array<CommentTableEntry> = [];
    const disputeEntry: Array<DisputeTableEntry> = [];
    const disputeSequence: string = this.billSequence!.substring(this.billSequence!.length - 4, this.billSequence!.length);
    this.selectedRows.forEach((row: RowNode) => {
      const data: IEBillCarDetails = row.data;
      const carInit: string = data.equipment.substring(0, data.equipment.length - 6);
      const carNumber: string = data.equipment.substring(data.equipment.length - 6, data.equipment.length);
      const commonObj: { billSequence: string; billType: string; carInit: string; carNumber: string; disputeSequence: string } = {
        billSequence: data.sequence,
        billType: data.billType,
        carInit,
        carNumber,
        disputeSequence
      };
      if (data.newOrderedDate) {
        disputeEntry.push({
          ...commonObj,
          dateType: 'OR',
          oldDate: NSCommons.formatNSDate(data.ordered, 'MM/dd/yyyy'),
          newDate: NSCommons.formatNSDate(data.newOrderedDate, 'MM/dd/yyyy')
        });
      }
      if (data.newConstraDate) {
        disputeEntry.push({
          ...commonObj,
          dateType: 'CP',
          oldDate: NSCommons.formatNSDate(data.constrPlaceDate, 'MM/dd/yyyy'),
          newDate: NSCommons.formatNSDate(data.newConstraDate, 'MM/dd/yyyy')
        });
      }
      if (data.newPlacedData) {
        disputeEntry.push({
          ...commonObj,
          dateType: 'PL',
          oldDate: NSCommons.formatNSDate(data.placed, 'MM/dd/yyyy'),
          newDate: NSCommons.formatNSDate(data.newPlacedData, 'MM/dd/yyyy')
        });
      }
      if (data.newReleasedDate) {
        disputeEntry.push({
          ...commonObj,
          dateType: 'RL',
          oldDate: NSCommons.formatNSDate(data.released, 'MM/dd/yyyy'),
          newDate: NSCommons.formatNSDate(data.newReleasedDate, 'MM/dd/yyyy')
        });
      }
      if (data.comment) {
        commentEntry.push({
          ...commonObj,
          comment: data.comment
        });
      }
    });
    if (disputeEntry.length === 0 && commentEntry.length === 0) {
      this.showMessage();
    } else {
      this.dispute.emit({
        commentTableEntry: commentEntry,
        disputeTableEntry: disputeEntry,
        commentCount: `${ commentEntry.length }`,
        disputeCount: `${ disputeEntry.length }`,
        function: 'DSPC'
      });
    }
  }

  /**
   * navigate Back
   *
   * @summary return to bill dispute details
   * @return void
   */
  navigateBack(): void {
    this.location.back();
  }

  /**
   * Reset Grid
   *
   * @summary reset cell selection
   * @return void
   */
  resetGrid(): void {
    this.grid.api.setRowData(this.carDetails);
    this.grid.api.refreshHeader();
    this.selectedRows = [];
  }

  private showMessage(): void {
    Swal.fire({
      title: NSMessages.warning,
      text: NSMessages.eBillCarDisputeMsg,
      icon: 'warning',
      confirmButtonText: 'OK',
      confirmButtonColor: EColors.GOLD,
      heightAuto: false
    });
  }
}
